import React from "react";
import { Modal, Badge } from "@/components/ui";
import {
  Calendar,
  User,
  ArrowUpRight,
  CreditCard,
  FileText,
  MessageSquare,
  Hash,
  CheckCircle2,
  Clock
} from "lucide-react";

export const ViewGeneralExpenseModal = ({ isOpen, onClose, expense, projects = [] }) => {
  if (!expense) return null;

  const DetailItem = ({ icon: Icon, label, value, color = "text-slate-500" }) => (
    <div className="flex items-start gap-3 group">
      <div className="mt-0.5 p-1.5 bg-slate-50 rounded-lg text-slate-400 group-hover:text-brand-blue group-hover:bg-blue-50 transition-colors">
        <Icon size={14} />
      </div>
      <div>
        <div className="text-[10px] font-bold text-slate-400 uppercase tracking-widest leading-none mb-1.5">{label}</div>
        <div className={`text-[13px] font-semibold ${color} leading-tight`}>{value || "—"}</div>
      </div>
    </div>
  );

  const statusColors = {
    pending: "bg-amber-50 text-amber-700 border-amber-100",
    approved: "bg-emerald-50 text-emerald-700 border-emerald-100",
    rejected: "bg-rose-50 text-rose-700 border-rose-100",
    partially_approved: "bg-blue-50 text-blue-700 border-blue-100"
  };

  const Card = ({ label, v, fullWidth = false }) => (
    <div className={`bg-slate-50 rounded-lg p-3 ${fullWidth ? 'col-span-2' : ''}`}>
      <div className="text-[10px] text-slate-500 mb-0.5 uppercase font-bold tracking-wider opacity-70">
        {label}
      </div>
      <div className="text-xs font-semibold text-brand-dark">
        {v !== undefined && v !== null && v !== "" ? String(v) : "—"}
      </div>
    </div>
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`Expense Details: #${expense.expenseId}`} size="max-w-lg">
      <div className="space-y-5">
        <div className="flex flex-wrap gap-2">
          <Badge status={expense.status} />
          <Badge status={expense.relatedToProject ? 'Project' : 'General'} custom="bg-orange-50 text-orange-600" />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Card label="Requested Amount" v={`₹${expense.amount.toLocaleString("en-IN")}`} />
          <Card label="Request Date" v={new Date(expense.date).toLocaleDateString("en-IN", { day: '2-digit', month: 'short', year: 'numeric' })} />

          <Card label="Requested By" v={expense.byWhomPaid} />
          <Card label="To Whom Paid" v={expense.toWhomPaid} />

          <Card label="Payment Method" v={expense.paymentMethod} />
          {expense.relatedToProject && (
            <>
              <Card label="Project ID" v={expense.displayProjectId} />
              <Card label="Customer Name" v={expense.projectCustomerName} />
            </>
          )}

          <Card label="Reason" v={expense.reason} fullWidth={true} />
          {expense.notes && (
            <Card label="Notes" v={expense.notes} fullWidth={true} />
          )}

          {expense.status !== 'pending' && (
            <>
              <div className="col-span-2 mt-2">
                <label className="text-[11px] font-bold text-slate-400 uppercase tracking-wider block pl-1 mb-2">
                  Administrative Outcome
                </label>
              </div>
              <Card label="Approved Amount" v={`₹${(expense.approvedAmount || expense.amount).toLocaleString("en-IN")}`} />
              <Card label="Final Status" v={expense.status?.replace('_', ' ')} />

              {expense.ownerMessage && (
                <Card label="Feedback from Owner" v={expense.ownerMessage} fullWidth={true} />
              )}
            </>
          )}

          {expense.status === 'paid' && (
            <>
              <div className="col-span-2 mt-2 pt-2 border-t border-slate-100">
                <label className="text-[11px] font-bold text-emerald-600 uppercase tracking-wider block pl-1 mb-2 flex items-center gap-1.5">
                  <CreditCard size={14} /> Disbursement Details
                </label>
              </div>
              {expense.transactionId && <Card label="Transaction ID / Ref" v={expense.transactionId} />}
              {expense.bankName && <Card label="Bank / Issuing Bank" v={expense.bankName} />}
              {expense.branch && <Card label="Branch" v={expense.branch} />}
              {expense.chequeNumber && <Card label="Cheque Number" v={expense.chequeNumber} />}
              {expense.chequeDate && <Card label="Cheque Date" v={new Date(expense.chequeDate).toLocaleDateString("en-IN", { day: '2-digit', month: 'short', year: 'numeric' })} />}
              {expense.upiPhone && <Card label="UPI Phone" v={expense.upiPhone} />}
              {expense.receiverName && <Card label="Receiver Name" v={expense.receiverName} />}
              {expense.receiverPhone && <Card label="Receiver Phone" v={expense.receiverPhone} />}
              {expense.disbursementNotes && <Card label="Disbursement Notes" v={expense.disbursementNotes} fullWidth={true} />}
            </>
          )}
        </div>
      </div>
    </Modal>
  );
};
